import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { supabase } from '../../../shared/lib/supabase';
import Logo from '../../../shared/components/ui/Logo';

interface Organization {
  id: string;
  name: string;
  subdomain: string;
  logo: string;
}

interface UserOrganization {
  organisation_id: string;
  role: string;
  is_primary: boolean;
  organisation: Organization;
}

const OrganizationSelectionScreen: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userOrganizations, setUserOrganizations] = useState<UserOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  // Load user's organizations
  useEffect(() => {
    const loadUserOrganizations = async () => {
      if (!user) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get user's organizations with organization details
        const { data, error: orgError } = await supabase
          .from('user_organisations')
          .select(`
            organisation_id,
            role,
            is_primary,
            organisation:organisations (
              id,
              name,
              subdomain,
              logo
            )
          `)
          .eq('user_id', user.id)
          .eq('active', true)
          .order('is_primary', { ascending: false });

        if (orgError) {
          console.error('Error loading user organizations:', orgError);
          setError('Failed to load organizations');
          return;
        }

        if (!data || data.length === 0) {
          setError('No organizations found for this user. Please contact your administrator to get access to an organization.');
          return;
        }

        setUserOrganizations(data as unknown as UserOrganization[]);
      } catch (err) {
        console.error('Error loading organizations:', err);
        setError('Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };

    loadUserOrganizations();
  }, [user]);

  // Handle organization selection
  const handleOrganizationSelect = (orgId: string) => {
    console.log('🎯 Selecting organization:', orgId);
    navigate(`/o/${orgId}`, { replace: true });
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="text-red-600 mb-4">
              <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Organizations</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Sticky */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
                    <Logo size="md" variant="full" />
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLogout}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                title="Logout"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Select Organization</h2>
          <p className="text-lg text-gray-600">
            Choose the organization you want to work with
          </p>
        </div>

        {/* Organization Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {userOrganizations.map((userOrg) => (
            <div
              key={userOrg.organisation_id}
              onClick={() => handleOrganizationSelect(userOrg.organisation_id)}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-200 cursor-pointer group"
            >
              {/* Organization Logo and Name */}
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center border border-blue-200">
                  {userOrg.organisation.logo ? (
                    <img
                      src={userOrg.organisation.logo}
                      alt={userOrg.organisation.name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="text-blue-600 font-bold text-xl">
                      {userOrg.organisation.name.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                    {userOrg.organisation.name}
                  </h3>
                  <p className="text-sm text-gray-500 truncate">
                    {userOrg.organisation.subdomain}
                  </p>
                </div>
              </div>
              
              {/* Role and Status Badges */}
              <div className="flex items-center justify-between mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {userOrg.role.replace('_', ' ').toUpperCase()}
                </span>
                {userOrg.is_primary && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Primary
                  </span>
                )}
              </div>
              
              {/* Action Button */}
              <div className="flex items-center justify-center text-blue-600 group-hover:text-blue-700 bg-blue-50 group-hover:bg-blue-100 rounded-lg py-2 transition-all">
                <span className="text-sm font-medium">Select Organization</span>
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* Help Text */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Need access to another organization? Contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSelectionScreen;
